import React, { useState } from 'react';

declare const require: {
  context(path: string, recursive: boolean, regExp: RegExp): {
    keys(): string[];
    <T>(id: string): T;
  };
};

interface Album {
  name: string;
  images: string[];
}

function Gallery() {
  const [selectedAlbum, setSelectedAlbum] = useState<string | null>(null);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());

  // Get all images from all folders
  const allImages = require.context('/public/images', true, /\.(png|jpg|jpeg)$/);
  
  // Organize images by album (folder)
  const albums: { [key: string]: Album } = {};
  allImages.keys().forEach((key: string) => {
    const parts = key.split('/').filter((p: string) => p && p !== '.');
    // Only include images that are in a subfolder (album), not in root
    if (parts.length >= 2) {
      const albumName = parts[0];
      const imagePath = allImages(key) as string;
      
      if (!albums[albumName]) {
        albums[albumName] = { name: albumName, images: [] };
      }
      albums[albumName].images.push(imagePath);
    }
  });

  const albumList = Object.values(albums);

  const handleImageLoad = (imagePath: string) => {
    setLoadedImages(prev => new Set(prev).add(imagePath));
  };

  const stripImageMetadata = async (imageSrc: string): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to strip metadata'));
            }
          }, 'image/jpeg', 0.95);
        }
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = imageSrc;
    });
  };

  const handleDownload = async (src: string, filename: string) => {
    try {
      const cleanImageBlob = await stripImageMetadata(src);
      const blobUrl = URL.createObjectURL(cleanImageBlob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      sessionStorage.clear();
      localStorage.clear();
      
      console.log('Full-size photo downloaded with metadata removed');
    } catch (error) {
      console.error('Error downloading photo:', error);
      const link = document.createElement('a');
      link.href = src;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Show album list
  if (!selectedAlbum) {
    if (albumList.length === 0) {
      return (
        <div className="bg-bg-card border border-border-color rounded-2xl p-12 text-center">
          <p className="text-text-secondary text-lg">
            No albums found. Create folders inside /public/images/
          </p>
        </div>
      );
    }

    return (
      <div>
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-text-primary mb-6">Albums</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {albumList.map((album, idx) => (
              <div
                key={idx}
                onClick={() => setSelectedAlbum(album.name)}
                className="bg-bg-card border border-border-color rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow duration-300"
              >
                <div className="relative h-48 bg-gray-700 overflow-hidden">
                  {album.images.length > 0 && (
                    <img
                      src={album.images[0]}
                      alt={album.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end">
                    <div className="w-full p-4">
                      <h3 className="text-white font-semibold text-lg capitalize">
                        {album.name}
                      </h3>
                      <p className="text-gray-300 text-sm">{album.images.length} photos</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show selected album
  const currentAlbum = albums[selectedAlbum];

  return (
    <div>
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => setSelectedAlbum(null)}
          className="px-4 py-2 bg-bg-card border border-border-color rounded-lg text-text-primary hover:bg-opacity-80 transition-colors"
        >
          ← Back to Albums
        </button>
        <h2 className="text-2xl font-bold text-text-primary capitalize">{selectedAlbum}</h2>
        <span className="text-text-secondary">({currentAlbum.images.length} photos)</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {currentAlbum.images.map((src: string, idx: number) => (
          <div key={idx} className="overflow-hidden rounded-lg shadow-lg relative group bg-bg-card">
            <img 
              src={src}
              alt={`${selectedAlbum} ${idx + 1}`} 
              className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300 cursor-pointer blur-sm"
              loading="lazy"
            />
            <img 
              src={src}
              alt={`${selectedAlbum} ${idx + 1}`} 
              className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300 cursor-pointer absolute inset-0"
              loading="lazy"
              onLoad={() => handleImageLoad(src)}
              style={{ filter: loadedImages.has(src) ? 'none' : 'blur(10px)' }}
            />
            <button
              onClick={() => handleDownload(src, `${selectedAlbum}-${idx + 1}.jpg`)}
              className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100"
            >
              <span className="text-white text-sm font-semibold px-4 py-2 bg-black bg-opacity-70 rounded-lg hover:bg-opacity-90">
                Download
              </span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Gallery;
